import React, {useMemo, ReactNode} from 'react';
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";

interface SimpleSelectOption {
  value: string | null | undefined;
  label: string;
}

interface SimpleSelectProps {
  id?: string;
  label?: string;
  name?: string;
  onChange?: (event: SelectChangeEvent) => void;
  options?: SimpleSelectOption[];
  placeholder?: string;
  required?: boolean;
  displayEmpty?: boolean;
  native?: boolean;
  value?: string;
}

/**
 * A Simple Select component that supports string and boolean values
 * @param id the id of the select
 * @param label the text to display above the select
 * @param name the name of the select element
 * @param required whether the select is required
 * @param value the currently selected value
 * @param onChange the function to call when the value changes
 * @param options the list of options to display in the select
 * @param placeholder the placeholder text to display when no value is selected. Will display only if selectProps.displayEmpty is true
 * @param displayEmpty whether to display the placeholder text when no value is selected
 * @param native render a native <select> (OS picker). Use on touch devices: iOS Safari can fail to paint
 * the MUI menu when it opens while the on-screen keyboard is closing.
 * @constructor
 */
const SimpleSelect = ({
  id = "select",
  label = "Select",
  name = "",
  onChange = () => {},
  options = [],
  placeholder = "Select a value",
  displayEmpty = false,
  required = false,
  native = false,
  value = "",
}: SimpleSelectProps) => {
  const memoizedOptions = useMemo(() => {
    const Option = native ? 'option' : MenuItem;
    return options.map((item, index) => (
      <Option value={item.value ?? ''} key={item.value ?? `option-${index}`}>
        {item.label}
      </Option>
    ))
  }, [options, native]);

  const renderValue = (value: string): ReactNode => {
    if (!displayEmpty) return value;
    // Find the matching option and display its label
    const matchingOption = options.find(option => option.value === value || (value === '' && option.value === ''));
    if (matchingOption) {
      return matchingOption.label;
    }
    // Fallback to placeholder if no matching option found
    if (value === null || value === undefined || value === '') return placeholder;
    return value;
  }

  const isEmpty = !value || value === '';
  const shouldShrinkLabel = !isEmpty || displayEmpty;

  return (
    <FormControl variant='standard' required={required} fullWidth>
      <InputLabel htmlFor={id} shrink={native || shouldShrinkLabel}>{label}</InputLabel>
      <Select
        native={native}
        id={id}
        onChange={onChange}
        value={value}
        variant={'standard'}
        name={name}
        renderValue={native ? undefined : renderValue}
        displayEmpty={native ? undefined : displayEmpty}
        inputProps={{'aria-label': label}}
      >
        {native && displayEmpty && <option value="" disabled>{placeholder}</option>}
        {memoizedOptions}
      </Select>
    </FormControl>
  );
};

export default SimpleSelect;
